// src/pages/QuotaList.js - Liste des achats de quotas
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const QuotaList = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [achats, setAchats] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState(null);
  const [serverError, setServerError] = useState("");
  const [stats, setStats] = useState({
    total_achats: 0,
    total_depense: 0,
    par_type: {}
  });

  // Filtres
  const [filters, setFilters] = useState({
    type: "",
    date_debut: "",
    date_fin: "",
    expire_avant: ""
  });

  const [showFilters, setShowFilters] = useState(false);

  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    success: '#4caf50',
    warning: '#ff9800',
    danger: '#f44336',
    info: '#2196f3',
    lightBg: '#F5F7FA',
    darkText: '#2E2E2E'
  };

  // Types de quotas avec leurs couleurs et prix
  const TYPES_QUOTA = [
    { value: 'client', label: 'Clients', icon: 'bi-people', color: '#2196f3', prix: 5000 },
    { value: 'site', label: 'Sites', icon: 'bi-building', color: '#4caf50', prix: 10000 },
    { value: 'tag_qr', label: 'Tags QR', icon: 'bi-qr-code', color: '#ff9800', prix: 2000 },
    { value: 'tag_nfc', label: 'Tags NFC', icon: 'bi-wifi', color: '#9c27b0', prix: 3000 }
  ];

  // Récupérer les infos utilisateur
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        const profile = String(user.profile || "").trim();
        setCurrentProfile(profile);
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
  }, []);

  // Charger les sociétés pour le filtre (admin seulement)
  useEffect(() => {
    if (currentProfile === "1") {
      fetchSocietes();
    }
  }, [currentProfile]);

  // Charger les achats
  useEffect(() => {
    if (currentProfile) {
      fetchAchats();
    }
  }, [currentProfile, selectedSociete, filters]);

  const fetchSocietes = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_BASE_URL}/societes/list.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.success) {
        setSocietes(response.data.societes);
      }
    } catch (err) {
      console.error("Erreur chargement sociétés:", err);
    }
  };

  const fetchAchats = async () => {
    setLoading(true);
    setServerError("");

    try {
      const token = localStorage.getItem("access_token");
      
      // Construire l'URL avec les filtres
      let url = `${API_BASE_URL}/achat_quota/list.php?`;
      
      if (currentProfile === "3") {
        // Gestionnaire : sa société automatiquement
        if (currentUser?.societe_id) {
          url += `id_societe=${currentUser.societe_id}&`;
        }
      } else if (currentProfile === "1" && selectedSociete) {
        // Admin : société sélectionnée
        url += `id_societe=${selectedSociete}&`;
      }

      // Ajouter les autres filtres
      if (filters.type) url += `type=${filters.type}&`;
      if (filters.date_debut) url += `date_debut=${filters.date_debut}&`;
      if (filters.date_fin) url += `date_fin=${filters.date_fin}&`;
      if (filters.expire_avant) url += `expire_avant=${filters.expire_avant}&`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        setAchats(response.data.achats);
        calculerStats(response.data.achats);
      } else {
        setServerError("Erreur lors du chargement des achats");
      }
    } catch (err) {
      console.error("Erreur chargement achats:", err);
      setServerError("Erreur lors du chargement des achats");
    } finally {
      setLoading(false);
    }
  };

  const calculerStats = (achatsData) => {
    let total = 0;
    let parType = {};

    achatsData.forEach(achat => {
      const prix = TYPES_QUOTA.find(t => t.value === achat.type)?.prix || 0;
      const montant = achat.quantite * prix;
      total += montant;

      if (!parType[achat.type]) {
        parType[achat.type] = {
          quantite: 0,
          montant: 0
        };
      }
      parType[achat.type].quantite += achat.quantite;
      parType[achat.type].montant += montant;
    });

    setStats({
      total_achats: achatsData.length,
      total_depense: total,
      par_type: parType
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      type: "",
      date_debut: "",
      date_fin: "",
      expire_avant: ""
    });
    setSelectedSociete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateSimple = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (achat) => {
    if (achat.statut_paiement === 'pending') {
      return <span className="badge" style={{ backgroundColor: COLORS.warning, padding: '6px 12px' }}>En attente de paiement</span>;
    }
    if (achat.est_expire) {
      return <span className="badge" style={{ backgroundColor: COLORS.danger, padding: '6px 12px' }}>Expiré</span>;
    }
    if (achat.expire_dans) {
      const jours = parseInt(achat.expire_dans);
      if (jours < 30) {
        return <span className="badge" style={{ backgroundColor: COLORS.warning, padding: '6px 12px' }}>Expire bientôt</span>;
      }
      return <span className="badge" style={{ backgroundColor: COLORS.success, padding: '6px 12px' }}>Actif</span>;
    }
    return <span className="badge" style={{ backgroundColor: COLORS.success, padding: '6px 12px' }}>Actif</span>;
  };

  const getTypeIcon = (type) => {
    const t = TYPES_QUOTA.find(t => t.value === type);
    return t ? <i className={`bi ${t.icon}`} style={{ color: t.color }}></i> : null;
  };

  return (
    <Layout>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>
              <i className="bi bi-cart-check me-2"></i>
              Historique des achats
            </h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Tableau de bord</Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
                  Quotas achetés
                </li>
              </ol>
            </nav>
          </div>
          <Link
            to="/achat-quota"
            className="btn fw-semibold"
            style={{
              backgroundColor: COLORS.success,
              color: 'white',
              borderRadius: '8px',
              padding: '10px 24px',
              border: 'none',
              boxShadow: '0 2px 6px rgba(76, 175, 80, 0.3)'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Nouvel achat
          </Link>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            {/* Cartes de statistiques */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: COLORS.info + '20',
                        color: COLORS.info
                      }}>
                        <i className="bi bi-cart-check fs-4"></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Total achats</h6>
                        <h4 className="fw-bold mb-0" style={{ color: COLORS.primary }}>
                          {stats.total_achats}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card" style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: COLORS.success + '20',
                        color: COLORS.success
                      }}>
                        <i className="bi bi-coin fs-4"></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Dépense totale</h6>
                        <h4 className="fw-bold mb-0" style={{ color: COLORS.primary }}>
                          {stats.total_depense.toLocaleString('fr-FR')} FCFA
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card" style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">Répartition par type</h6>
                    <div className="d-flex gap-3">
                      {Object.entries(stats.par_type).map(([type, data]) => {
                        const t = TYPES_QUOTA.find(t => t.value === type);
                        return (
                          <div key={type} className="text-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-1" style={{
                              width: '32px',
                              height: '32px',
                              backgroundColor: t?.color + '20',
                              color: t?.color
                            }}>
                              <i className={`bi ${t?.icon}`}></i>
                            </div>
                            <small className="d-block fw-bold">{data.quantite}</small>
                            <small className="text-muted">{data.montant.toLocaleString('fr-FR')} F</small>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtres */}
            <div className="card mb-4" style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: COLORS.primary }}>
                    <i className="bi bi-funnel me-2"></i>
                    Filtres
                  </h6>
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowFilters(!showFilters)}
                    style={{ color: COLORS.secondary }}
                  >
                    <i className={`bi bi-chevron-${showFilters ? 'up' : 'down'}`}></i>
                  </button>
                </div>

                {showFilters && (
                  <div className="row g-3">
                    {currentProfile === "1" && (
                      <div className="col-md-3">
                        <label className="form-label small">Société</label>
                        <select
                          className="form-select form-select-sm"
                          value={selectedSociete || ""}
                          onChange={(e) => setSelectedSociete(e.target.value || null)}
                        >
                          <option value="">Toutes les sociétés</option>
                          {societes.map(s => (
                            <option key={s.id_societe} value={s.id_societe}>
                              {s.nom_societe}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="col-md-2">
                      <label className="form-label small">Type</label>
                      <select
                        className="form-select form-select-sm"
                        name="type"
                        value={filters.type}
                        onChange={handleFilterChange}
                      >
                        <option value="">Tous</option>
                        {TYPES_QUOTA.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Date début</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="date_debut"
                        value={filters.date_debut}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Date fin</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="date_fin"
                        value={filters.date_fin}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Expire avant</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="expire_avant"
                        value={filters.expire_avant}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <div className="col-md-1 d-flex align-items-end">
                      <button
                        className="btn btn-sm"
                        onClick={resetFilters}
                        style={{ color: COLORS.secondary }}
                      >
                        <i className="bi bi-arrow-counterclockwise"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tableau des achats */}
            <div className="card" style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <div className="card-body">
                {serverError && (
                  <div className="alert alert-danger" style={{ borderRadius: '8px' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {serverError}
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="text-muted">Chargement des achats...</p>
                  </div>
                ) : achats.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bi bi-cart-x" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    </div>
                    <h6 style={{ color: COLORS.darkText }}>Aucun achat trouvé</h6>
                    <p className="text-muted mb-3">
                      {currentProfile === "1" 
                        ? "Commencez par effectuer un achat de quotas"
                        : "Votre société n'a pas encore effectué d'achat de quotas"
                      }
                    </p>
                    <Link
                      to="/achat-quota"
                      className="btn fw-semibold"
                      style={{
                        backgroundColor: COLORS.success,
                        color: 'white',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        border: 'none'
                      }}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Premier achat
                    </Link>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead style={{ backgroundColor: COLORS.lightBg }}>
                        <tr>
                          <th className="ps-3">Date</th>
                          {currentProfile === "1" && <th>Société</th>}
                          <th>Type</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-end">Montant</th>
                          <th>Expiration</th>
                          <th className="text-center">Statut</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {achats.map((achat) => {
                          const type = TYPES_QUOTA.find(t => t.value === achat.type);
                          const montant = achat.quantite * (type?.prix || 0);
                          
                          return (
                            <tr key={achat.id}>
                              <td className="ps-3">
                                <div>{formatDateSimple(achat.date_achat)}</div>
                                <small className="text-muted">
                                  {new Date(achat.date_achat).toLocaleTimeString('fr-FR')}
                                </small>
                              </td>
                              {currentProfile === "1" && (
                                <td>
                                  <span className="fw-bold">{achat.societe}</span>
                                </td>
                              )}
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{getTypeIcon(achat.type)}</span>
                                  <span>{type?.label || achat.type}</span>
                                </div>
                              </td>
                              <td className="text-center fw-bold">{achat.quantite}</td>
                              <td className="text-end fw-bold">
                                {montant.toLocaleString('fr-FR')} FCFA
                              </td>
                              <td>
                                {achat.expiration ? (
                                  <div>
                                    <div>{formatDateSimple(achat.expiration)}</div>
                                    {achat.expire_dans && !achat.est_expire && (
                                      <small className="text-muted">
                                        ({achat.expire_dans})
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="text-center">
                                {getStatusBadge(achat)}
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm"
                                  style={{ color: COLORS.secondary }}
                                  onClick={() => {
                                    // Voir les détails de l'achat
                                    console.log("Détails achat:", achat);
                                  }}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .table th {
          font-weight: 600;
          color: ${COLORS.darkText};
          border-bottom-width: 1px;
        }
        .table td {
          vertical-align: middle;
        }
        .badge {
          font-weight: 500;
          border-radius: 6px;
        }
        .btn:hover {
          transform: translateY(-1px);
          transition: all 0.2s ease;
        }
      `}</style>
    </Layout>
  );
};

export default QuotaList;